import type { MailProvider } from "@don/wire";

import { GETTING_STARTED_URL } from "../shell/recorder-links.js";

/**
 * The one table of provider words (epic 26-10-28-outlook-connector design-spec §6, decision 5):
 * each provider's display name, its company, and the `data-connect-row` hook its row carries.
 * Every string that names a provider reads it from here (issue 462).
 */
export const PROVIDERS = {
  google: { name: "Gmail", company: "Google", row: "gmail" },
  microsoft: { name: "Outlook", company: "Microsoft", row: "outlook" },
} as const satisfies Record<MailProvider, { name: string; company: string; row: string }>;

/** The other provider, for the switch line. */
export const OTHER: Record<MailProvider, MailProvider> = { google: "microsoft", microsoft: "google" };

/** The provider-keyed words of the Outlook design-spec §6.1 (issue 462). */
export const PROVIDER_COPY = {
  connect: (p: MailProvider) => `Connect ${PROVIDERS[p].name}`,
  handoff: (p: MailProvider) => `Opening ${PROVIDERS[p].company}…`,
  switchLine: (p: MailProvider) => `Switching to ${PROVIDERS[OTHER[p]].name}? Disconnect ${PROVIDERS[p].name} first.`,
  // The guess line under the guessed row (issue 464, §6.1).
  guess: (p: MailProvider) => `Your work email looks like it’s on ${PROVIDERS[p].name}.`,
  guessPersonal: `Your email looks like it’s on ${PROVIDERS.microsoft.name}.`,
  // A callback's outcome under its provider's row (issue 463, §6.1); Gmail's are epic A's words.
  cancelled: (p: MailProvider) => `You didn’t allow access on ${PROVIDERS[p].company}’s screen, so ${PROVIDERS[p].name} isn’t connected.`,
  scopesLead: (p: MailProvider) => `Don needs every permission on ${PROVIDERS[p].company}’s screen.`,
  mismatchLead: (p: MailProvider) => `This ${PROVIDERS[p].company} account isn’t yours in Don.`,
  mismatchRest: (p: MailProvider, tried: string, expected: string) =>
    `You signed in to ${PROVIDERS[p].company} as ${tried}. Sign in as ${expected}.`,
  connectFailedLead: (p: MailProvider) => `Couldn’t connect ${PROVIDERS[p].name}.`,
  connectFailedRest: (p: MailProvider) => `${PROVIDERS[p].company} didn’t answer. Try again in a minute.`,
  // A lapsed Connection's why (issue 468, §6.1): Gmail's is epic A's testing line, Outlook's names Microsoft.
  lapsedWhy: (p: MailProvider) =>
    p === "google"
      ? `${PROVIDERS[p].company} asks you to reconnect every 7 days while Don is in testing.`
      : `${PROVIDERS[p].company} stopped accepting Don’s access. This happens after about 90 days unused, a password change, or when your organization asks for a fresh sign-in or removes Don.`,
  // The start route's one-Connection refusal (issue 463, D8); `p` is the provider held.
  refusedTitle: (p: MailProvider) => `Disconnect ${PROVIDERS[p].name} first`,
  refusedLine: (p: MailProvider) =>
    `Don works with one email account at a time. Disconnect ${PROVIDERS[p].name} here, then connect ${PROVIDERS[OTHER[p]].name}.`,
  // Disconnect (issue 469, §6.1): epic A's as-built words (issue 440 keeps the voice), keyed.
  disconnectTitle: (p: MailProvider) => `Disconnect ${PROVIDERS[p].name}?`,
  disconnected: (p: MailProvider) =>
    `${PROVIDERS[p].name} disconnected. Don no longer reads your mail and has deleted the mail it was learning from. It keeps what it learned about how you write.`,
  disconnectFailed: (p: MailProvider) => `Couldn’t disconnect ${PROVIDERS[p].name}. Try again in a minute.`,
  // Outlook's second line (PRD §2 "Revoke", decision 34): who else can remove Don. Plain text.
  disconnectedAdmin: `Your IT admin can also remove Don from your organization’s ${PROVIDERS.microsoft.company} account.`,
  disconnectedPersonal: `You can also remove Don from your ${PROVIDERS.microsoft.company} account at account.live.com/consent/Manage.`,
};

/**
 * The hosted guide for IT admins (Outlook design-spec §5.0, §6.1), under the recorder's
 * getting-started guide (the one place that URL is spelled, `tests/recorder-links.test.tsx`).
 */
export const GUIDE_URL = `${GETTING_STARTED_URL}/outlook`;

const M365 = `${PROVIDERS.microsoft.company} 365`;

/**
 * The admin approval words of Outlook design-spec §6.1 (issue 465), verbatim. `noteLead` and
 * `noteGuide` are the note's two parts around the admin link; {@link adminNote} joins them.
 */
export const ADMIN_COPY = {
  disclosureBefore: "Your IT admin may need to approve Don",
  disclosureNeeded: "Action needed from your IT admin",
  preAttempt: `Most ${M365} organizations ask IT to approve Don first. If ${PROVIDERS.microsoft.company} asks for approval, send your admin this note.`,
  copy: "Copy a note for your IT admin",
  copyAgain: "Copy the note again",
  noteLead: `Could you approve Don for our ${M365}? It reads my email and contacts to draft emails in my voice, and sends only when I press Send. Approving covers anyone here who connects Don. Open this link as an admin and choose Accept:`,
  noteGuide: `Steps and help for IT admins: ${GUIDE_URL}`,
  noteLabel: "Note for your IT admin",
  fallback: "Your browser didn’t allow copying, so the note is selected. Press ⌘C to copy it.",
  waitingStatus: "Waiting for your IT admin to approve Don",
  waitingWhy: "Sent them the note? Once they approve, press Try again.",
  tryAgain: "Try again",
  waitingLine: `${M365} lets only your IT admin approve apps that read and send mail. One approval covers anyone here who connects Don.`,
  requiredStatus: "Your organization needs to approve Don first",
  requiredLine: `${M365} lets only your IT admin approve apps that read and send mail. If ${PROVIDERS.microsoft.company} showed you a Request approval button, pressing it sends them the request too.`,
  scopesStatus: "Your organization approved only part of what Don needs.",
  scopesLine: "Your IT admin can approve the rest with the same link.",
  scopesAction: "Connect again",
  guide: "Open the guide for your IT admin",
  // The blocked state (issue 468, §6.1): a lapse, or a connect, refused by sign-in rules.
  blockedStatus: "Blocked by your organization",
  blockedLine: "Your organization’s sign-in rules stop Don from reaching your mail. Reconnecting won’t fix this.",
} as const;

/**
 * The admin callback page's words, verbatim from Outlook design-spec §6.2 (issue 466): the card
 * Microsoft's admin consent lands on, one per result.
 */
export const ADMIN_PAGE_COPY = {
  approvedTitle: "Don is approved for your organization",
  approvedBody: `Ask your team to connect ${PROVIDERS.microsoft.name} from Don. You can close this tab.`,
  failedTitle: "Don isn’t approved yet",
  failedBody: `${PROVIDERS.microsoft.company} didn’t finish the approval. Open the link from your colleague again.`,
  unknownTitle: (domain: string) => `${PROVIDERS.microsoft.company} doesn’t recognize ${domain} as an organization.`,
  unknownBody: "Ask your colleague for a new link.",
} as const;

/** The note copied for the IT admin (§6.1): the lead, the link, a blank line, the guide line. */
export const adminNote = (link: string) => `${ADMIN_COPY.noteLead} ${link}\n\n${ADMIN_COPY.noteGuide}`;

/**
 * The Connections page's words, verbatim from epic 26-10-27-person-files-voice design-spec §6.1.
 * Curly apostrophes and the single-character ellipsis are the spec's. Later slices add their
 * states' keys here.
 */
export const COPY = {
  tabTitle: "Don · Connections",
  /** The admin callback page's tab (Outlook design-spec §2.2, issue 466). */
  adminTabTitle: "Don",
  header: "Connections",
  ephemeral: "This page only manages your connections. Everything else happens in the Don pill.",
  provider: PROVIDERS.google.name,
  opening: "Opening your connections…",
  linkExpiredTitle: "This link has expired",
  linkExpiredBody: "Links from the Don pill work once, for 10 minutes.",
  sessionExpiredTitle: "This page timed out",
  sessionExpiredBody: "Connections signs you out after 30 minutes.",
  wayBack: "To open Connections again, right-click the Don pill and choose Connections…",
  notConnected: "Not connected",
  // The mechanism: since issue 462 one intro line above the rows (Outlook design-spec W2).
  why: "Lets Don learn how you write from mail you’ve sent, look up your contacts and write drafts. Nothing is sent without your click.",
  connect: PROVIDER_COPY.connect("google"),
  handoff: PROVIDER_COPY.handoff("google"),
  connectedAs: "Connected as ",
  since: "Since ",
  disconnect: "Disconnect",
  reconnectBy: " · Reconnect by ",
  testing: PROVIDER_COPY.lapsedWhy("google"),
  reconnect: "Reconnect",
  lapsed: "Needs renewing",
  close: "You can close this tab.",
  cancelled: PROVIDER_COPY.cancelled("google"),
  scopesLead: PROVIDER_COPY.scopesLead("google"),
  scopesRest: "Connect again and leave every box ticked.",
  scopesAction: "Connect again",
  mismatchLead: PROVIDER_COPY.mismatchLead("google"),
  mismatchRest: (tried: string, expected: string) => PROVIDER_COPY.mismatchRest("google", tried, expected),
  mismatchAction: "Use another account",
  connectFailedLead: PROVIDER_COPY.connectFailedLead("google"),
  connectFailedRest: PROVIDER_COPY.connectFailedRest("google"),
  connectFailedAction: "Try again",
  disconnectTitle: PROVIDER_COPY.disconnectTitle("google"),
  disconnectBody:
    "Don stops reading your mail right away and deletes its connection and the mail it was learning from. It keeps what it learned about how you write, including writing rules you gave it in Chat.",
  disconnectCancel: "Cancel",
  disconnectConfirm: "Disconnect",
  disconnecting: "Disconnecting…",
  disconnected: PROVIDER_COPY.disconnected("google"),
  disconnectFailed: PROVIDER_COPY.disconnectFailed("google"),
  // Not in §6.1: the pill panel's failure line (§6.2), for a first read that failed outright.
  failed: "Couldn’t open Connections. Try again in a minute.",
} as const;
